import { useEffect, useState } from 'react';
import type React from 'react';
import { cx } from '@/styled-system/css';
import { immersiveContainer, immersiveContainerClosing } from './immersiveContainer.styles';

const TRANSITION_DURATION_MS = 300;

export type ImmersiveContainerProps = {
  children: React.ReactNode;
  isClosing?: boolean;
  onAnimationComplete?: () => void;
};

/**
 * ImmersiveContainer - Sidebar-width container for immersive HDP mode
 *
 * Displays as:
 * - Mobile: Full-screen (100vw)
 * - Tablet (md): 452px sidebar aligned right
 * - Desktop (lg+): 880px sidebar aligned right
 *
 * Includes slide-down transition when closing.
 */
export const ImmersiveContainer = ({ children, isClosing = false, onAnimationComplete }: ImmersiveContainerProps) => {
  const [shouldRender, setShouldRender] = useState(true);

  // Prevent body scroll while the overlay is mounted (mobile has no
  // parent overflow:hidden container like SplitMapSidebar on desktop).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        setShouldRender(false);
        onAnimationComplete?.();
      }, TRANSITION_DURATION_MS);

      return () => clearTimeout(timer);
    }
  }, [isClosing, onAnimationComplete]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      data-testid="immersive-container"
      className={cx(immersiveContainer, isClosing && immersiveContainerClosing)}
      role="dialog"
      aria-modal="true"
    >
      {children}
    </div>
  );
};
